/**
 * User Matter Assignments Hook
 * Admin hook for fetching and updating matter assignments for a user
 */

import { useState, useCallback } from 'react';
import {
  useUserMatterAssignments as useUserMatterAssignmentsApi,
  useMatterAssignmentMutations,
} from '../api/echo';

export interface UseUserMatterAssignmentsResult {
  assignedMatterIds: number[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchAssignments: (userId: number) => void;
  updateAssignments: (userId: number, matterIds: number[]) => Promise<void>;
}

/**
 * Hook for managing a user's matter assignments (admin only).
 * Wraps the API hooks with convenience state management.
 */
export function useUserMatterAssignments(userId: number | null): UseUserMatterAssignmentsResult {
  const { data, loading, error, refetch } = useUserMatterAssignmentsApi(userId);
  const { updateAssignments: apiUpdateAssignments } = useMatterAssignmentMutations();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const assignedMatterIds = data?.assigned_matter_ids ?? [];

  const fetchAssignments = useCallback(
    (_userId: number) => {
      refetch();
    },
    [refetch]
  );

  const updateAssignments = useCallback(
    async (targetUserId: number, matterIds: number[]) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        await apiUpdateAssignments(targetUserId, matterIds);
        refetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update assignments';
        setSaveError(message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [apiUpdateAssignments, refetch]
  );

  return {
    assignedMatterIds,
    isLoading: loading,
    isSaving,
    error: saveError || (error ? error.message : null),
    fetchAssignments,
    updateAssignments,
  };
}

export default useUserMatterAssignments;
